<!-- src/App.vue -->
<template>
  <v-app :theme="isDark ? 'dark' : 'light'">

    <v-navigation-drawer v-model="drawer" v-if="!hideChrome">
      <v-list-item>
        <v-list-item-title class="title text-center pt-4 pb-3">
          <router-link
            to="/"
            style="font-size: 18px; text-decoration: none;"
            :class="{ 'dark-link': isDark, 'light-link': !isDark }"
          >{{ apptitle }}</router-link>
        </v-list-item-title>
      </v-list-item>
      <v-divider></v-divider>

      <v-list density="compact" nav>
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :prepend-icon="item.icon"
          :title="item.title"
          :to="item.to"
          link
        ></v-list-item>
      </v-list>

      <template #append>
        <div class="pa-3">
          <v-chip
            :color="roleColor"
            size="small"
            variant="tonal"
            prepend-icon="mdi-shield-account"
            class="w-100 justify-center"
          >
            {{ roleLabel }}
          </v-chip>
        </div>
      </template>
    </v-navigation-drawer>

    <v-app-bar
      v-if="!hideChrome"
      app dark prominent
      color="--color-background"
      height="64"
      image="top_pict.jpg"
    >
      <template v-slot:image>
        <v-img gradient="to top right, rgba(19,84,122,.8), rgba(128,208,199,.8)"></v-img>
      </template>
      <template v-slot:prepend>
        <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      </template>
      <v-app-bar-title>
        <router-link to="/" style="font-size: 24px; text-decoration: none; color: #555;">
          {{ apptitle }}
        </router-link>
      </v-app-bar-title>
      <v-spacer></v-spacer>
      <v-menu location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" icon>
            <v-avatar color="primary" size="36px">
              <span>{{ userInitials }}</span>
            </v-avatar>
          </v-btn>
        </template>

        <v-list>
          <v-list-item v-if="isSuperAdmin" :to="'/labels'" prepend-icon="mdi-label-multiple-outline">
            <v-list-item-title>{{ t('labels') }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isSuperAdmin" :to="'/devices'" prepend-icon="mdi-tablet-cellphone">
            <v-list-item-title>{{ t('devices') }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isSuperAdmin" :to="'/users'" prepend-icon="mdi-account-multiple">
            <v-list-item-title>{{ t('users') }}</v-list-item-title>
          </v-list-item>
          <v-list-item :to="'/settings'" prepend-icon="mdi-cog">
            <v-list-item-title>{{ t('settings') }}</v-list-item-title>
          </v-list-item>
          <v-list-item :to="'/about'" prepend-icon="mdi-information-variant">
            <v-list-item-title>{{ t('about') }}</v-list-item-title>
          </v-list-item>

          <!--
            Impersonation — a REAL god only.

            canImpersonate is false inside an impersonated session, so
            the entry point disappears the moment you step into an
            account. The API refuses the chain as well; hiding it here
            means nobody has to discover that by being refused.
          -->
          <template v-if="canImpersonate">
            <v-divider></v-divider>
            <v-list-item :to="'/impersonate'" prepend-icon="mdi-incognito" base-color="error">
              <v-list-item-title>{{ t('impersonation.menuItem') }}</v-list-item-title>
            </v-list-item>
          </template>

          <v-divider></v-divider>
          <v-list-item @click="handleLogout" prepend-icon="mdi-logout" base-color="red">
            <v-list-item-title>{{ t('logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <!--
        Outside hideChrome on purpose.

        The label designer runs without the app bar and the drawer, and
        that is fine — but it is also where somebody is most likely to
        change a customer's design while forgetting whose account they
        are in. The one piece of chrome that must never be hidden is the
        one saying which account this is.
      -->
      <ImpersonationBanner />
      <router-view></router-view>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n }    from 'vue-i18n';
import { useTheme }   from 'vuetify';
import { useStore }   from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import ImpersonationBanner from '@/components/ImpersonationBanner.vue';

const { t }   = useI18n();
const theme   = useTheme();
const store   = useStore();
const router  = useRouter();
const route   = useRoute();

const drawer   = ref(true);
const apptitle = ref(process.env.VUE_APP_NAME || 'KitchenPrint™');
const userId   = localStorage.getItem('userId') || 'default_user';
const themeKey = `${userId}_theme`;

// Hide nav drawer and global app bar on routes where they don't belong:
//   - login           : user not authenticated yet, no chrome
//   - label-designer  : runs fullscreen with its own toolbar
// Add additional route names to this list as needed (signup, password-reset, etc).
const hideChrome = computed(() => {
  const noChromeRoutes = ['login', 'label-designer'];
  return noChromeRoutes.includes(route.name);
});

const role            = computed(() => store.getters['auth/role'] || localStorage.getItem('userRole') || 'user');
const isSuperAdmin    = computed(() => store.getters['auth/isSuperAdmin']);
const isLocationAdmin = computed(() => store.getters['auth/isLocationAdmin']);
const canImpersonate  = computed(() => store.getters['auth/canImpersonate']);

const ROLE_HIERARCHY = { god: 4, superadmin: 3, location_admin: 2, user: 1 };
const hasRole = (minimum) => (ROLE_HIERARCHY[role.value] || 0) >= (ROLE_HIERARCHY[minimum] || 0);

const roleLabel = computed(() => {
  const labels = {
    god:            'God',
    superadmin:     t('admin'),
    location_admin: t('locationAdmin') || 'Location Admin',
    user:           t('users'),
  };
  return labels[role.value] || role.value;
});

const roleColor = computed(() => {
  const colors = { god: 'red', superadmin: 'orange', location_admin: 'blue', user: 'grey' };
  return colors[role.value] || 'grey';
});

const userName     = computed(() => store.state.auth.username || localStorage.getItem('username') || '');
const isDark       = computed(() => theme.global.current.value.dark);
const userInitials = computed(() => userName.value.split(' ').map(n => n.charAt(0)).join(''));

// Main drawer menu — day-to-day navigation. Admin-only entries (labels,
// devices, users) live in the avatar menu instead, so the drawer stays
// focused on what users do every day.
const menuItems = computed(() => {
  const items = [];
  items.push({ title: t('dashboard'), icon: 'mdi-monitor-dashboard',  to: '/' });
  items.push({ title: t('foodItems'), icon: 'mdi-food',               to: '/fooditems' });
  if (store.state.settings?.useCategories) {
    items.push({ title: t('categories'), icon: 'mdi-shape-outline', to: '/categories' });
  }
  if (hasRole('superadmin')) {
    items.push({ title: t('configurations'),            icon: 'mdi-cog-outline',           to: '/configurations' });
    items.push({ title: t('locations') || 'Locations',  icon: 'mdi-map-marker-multiple',   to: '/locations' });
   // items.push({ title: t('menus'),                     icon: 'mdi-silverware-fork-knife', to: '/menus' });
  }
  return items;
});

const handleLogout = async () => {
  try {
    // End the impersonation first, so the audit row gets its end time
    // rather than being left open. Logging out with a borrowed token
    // would discard the session silently and leave endedAt null
    // forever — and half of MobilePrint's rows are already like that.
    if (store.getters['auth/isImpersonating']) {
      await store.dispatch('auth/endImpersonation');
    }
    await store.dispatch('auth/logout');
    router.push({ name: 'login' });
  } catch (error) {
    console.error('Error logging out:', error);
  }
};
</script>

<style>
.dark-link  { color: #DDDDDD; }
.light-link { color: #333333; }
.theme-change {
  background: #3F51B5;
  max-width: 2.2em;
  max-height: 2.2em;
  margin-right: 0.75em;
}
</style>